import './mqttws31';

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import { AwsEnvironment } from '../aws/environment';
import { AwsSignature } from '../aws/signature';
import { User } from '../user/user';

declare var Paho: any;

@Injectable()
export class MQTTService {

    private connected = false;
    private connecting = false;
    private client: any;
    private clientId: string;
    private topicsMap = new Map<string, Subject<string>>();
    private topicSubscribers = new Map<string, number>();
    private connSubject: Subject<boolean>;

    constructor(private signature: AwsSignature, private awsEnv: AwsEnvironment) { }

    connect(user: User): Observable<boolean> {

        if (!this.connected && !this.connecting) {

            this.connSubject = new BehaviorSubject<boolean>(false);

            this.connecting = true;
            this.topicsMap.clear();
            this.topicSubscribers.clear();

            let url = `wss://${user.IoTEndpoint}/mqtt`;
            let request = {
                method: 'GET',
                url: url,
                headers: {},
                data: ''
            };
            let signedUrl = this.signature.signRequestUsingQueryString(request, this.awsEnv.getCredentials(), this.awsEnv.getIotRegion(), 'iotdevicegateway');

            this.clientId = this.createClientId(user);
            this.client = this.createClient(signedUrl, this.clientId);
            this.client.onConnectionLost = this.onConnectionLost;
            this.client.onMessageArrived = this.onMessageArrived;

            let connectOptions = {
                onSuccess: this.onConnectionSuccess,
                useSSL: true,
                timeout: 3,
                mqttVersion: 4,
                onFailure: this.onConnectionFailed
            };

            this.client.connect(connectOptions);
        }

        return this.connSubject.asObservable();
    }

    register(topic: string): Observable<string> {
        this.checkConnection();

        let topicSubject: Subject<string>;
        if (this.topicsMap.has(topic)) {
            topicSubject = this.topicsMap.get(topic);
            this.topicSubscribers[topic]++;
        } else {
            topicSubject = new BehaviorSubject<string>('');
            this.topicsMap.set(topic, topicSubject);
            this.topicSubscribers[topic] = 1;
            try {
                this.client.subscribe(topic);
            } catch (e) {
                this.topicsMap.delete(topic);
                throw e;
            }
        }

        return topicSubject.asObservable();
    }

    unregister(topic: string): void {
        this.checkConnection();

        if (this.topicsMap.has(topic)) {
            this.topicSubscribers[topic]--;
            if (this.topicSubscribers[topic] <= 0) {
                try {
                    let topicSubject = this.topicsMap.get(topic);
                    topicSubject.complete();
                    this.client.unsubscribe(topic);
                } finally {
                    this.topicsMap.delete(topic);
                }
            }
        }
    }

    publish(topic: string, data: any) {
        this.checkConnection();

        let message = new Paho.MQTT.Message(JSON.stringify(data));
        message.destinationName = topic;
        this.client.send(message);
    }

    getClientId(): string {
        this.checkConnection();
        return this.clientId;
    }

    /**
    * Will ne mocked during unit tests
    */
    createClient(signedUrl: string, clientId: string): any {
        return new Paho.MQTT.Client(signedUrl, clientId);
    }

    private createClientId(user: User): string {
        return `${user.Email}_${Math.random().toString(16).substr(2, 8)}`;
    }

    private checkConnection() {
        if (!this.connected) {
            throw 'Client is not connected';
        }
    }

    private onMessageArrived = (message: any) => {
        //console.log("onMessageArrived:" + message.payloadString);
        if (this.topicsMap.has(message.destinationName)) {
            let topicSubject: Subject<string> = this.topicsMap.get(message.destinationName);
            console.info('Received on MQTT channel: ' + message.destinationName + '-' + message.payloadString);
            topicSubject.next(message.payloadString);

        }
    }

    private onConnectionLost = (response: any) => {
        if (response.errorCode !== 0) {
            console.log('MQTT connection lost:' + response.errorMessage);
        }

        this.connected = false;
        this.connecting = false;
        this.topicsMap.clear();
        this.topicSubscribers.clear();
        this.client = null;
        this.connSubject.error(response.errorMessage);
    }

    private onConnectionSuccess = () => {
        console.log('MQTT connect succeded. ClientId=' + this.clientId);
        this.connected = true;
        this.connecting = false;
        this.connSubject.next(true);
    }

    private onConnectionFailed = (response: any) => {
        console.log('MQTT connect failed: code=' + response.errorCode + ' message=' + response.errorMessage);
        this.connected = false;
        this.connecting = false;
        this.topicsMap.clear();
        this.topicSubscribers.clear();
        this.client = null;
        this.connSubject.error(response.errorMessage);
    }

}



// WEBPACK FOOTER //
// ./src/app/mqtt/mqtt.service.ts